import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useQueryClient } from 'react-query';
import { authApi, tokenManager, AuthApiError, AuthUser, mfaApi } from '../services/authApi';
import { billingApi } from '../services/billingApi';
import { creditApi } from '../services/creditApi';
import { attemptSilentRefresh } from '../services/httpClient';
import { tokenStorage } from '../utils/tokenStorage';
import { clearAutoLogoutReason } from '../utils/autoLogoutReason';
import type { MfaChallengeResponse, MfaMethod } from '../types';
import { getDeviceFingerprint } from '../utils/deviceFingerprint';

const SESSION_BROADCAST_CHANNEL = 'clinlexis-session';

interface User {
  id: string;
  username: string;
  email: string;
  roles: string[];
  firstName?: string;
  lastName?: string;
  title?: string;
  customerNumber?: string;
  gmcNumber?: string;
  department?: string;
  personaType?: 'EXPERT_WITNESS' | 'SOLICITOR';
  consentsCompleted?: boolean;
  emailVerified?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAuthReady: boolean;
  hasActiveSubscription: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithTokens: (accessToken: string, refreshToken: string, user: import('../services/authApi').AuthUser) => void;
  signUp: (userData: any) => Promise<void>;
  logout: (options?: { preserveAutoLogoutReason?: boolean }) => void;
  refreshUser: () => Promise<void>;
  confirmSubscription: () => void;
  token: string | null;
  isImpersonating: boolean;
  impersonatedBy: string | null;
  // MFA
  mfaChallenge: MfaChallengeResponse | null;
  verifyMfa: (code: string, method: MfaMethod | 'BACKUP', trustDevice: boolean) => Promise<void>;
  sendMfaOtp: (method: MfaMethod) => Promise<void>;
  clearMfaChallenge: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

/** Thrown by login() when MFA verification is required before auth completes. */
export class MfaRequiredError extends Error {
  constructor() {
    super('MFA_REQUIRED');
    this.name = 'MfaRequiredError';
  }
}

const isAdmin = (u: User | AuthUser | null): boolean =>
  u != null && (('roles' in u ? u.roles : [u.role]).includes('ADMIN'));

// Helper function to convert AuthUser to User
const convertAuthUser = (authUser: AuthUser): User => ({
  id: authUser.id,
  username: authUser.email.split('@')[0], // Use email prefix as username
  email: authUser.email,
  roles: [authUser.role],
  firstName: authUser.firstName,
  lastName: authUser.lastName,
  title: authUser.title,
  gmcNumber: authUser.gmcNumber,
  department: authUser.department,
  personaType: authUser.personaType,
  consentsCompleted: authUser.consentsCompleted ?? false,
  emailVerified: authUser.emailVerified ?? false
});

/** Returns true if the user has a Stripe subscription OR a positive credit balance. */
const checkHasAccess = async (): Promise<boolean> => {
  try {
    const hasSub = await billingApi.hasActiveSubscription();
    if (hasSub) return true;
  } catch { /* fall through */ }
  try {
    const balance = await creditApi.getBalance();
    if (balance.freePageBalance > 0 || balance.hasActiveSubscription) return true;
  } catch { /* fall through */ }
  return false;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallengeResponse | null>(null);
  const [impersonatedBy, setImpersonatedBy] = useState<string | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshRetryCountRef = useRef(0);
  const MAX_PROACTIVE_RETRIES = 3;
  const PROACTIVE_RETRY_DELAY = 30_000; // 30s between retries

  // Schedule a proactive token refresh at ~80% of the JWT lifetime
  const scheduleTokenRefresh = (accessToken: string) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshRetryCountRef.current = 0; // reset retry count for new token
    scheduleRefreshAttempt(accessToken);
  };

  const scheduleRefreshAttempt = (accessToken: string) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      const expiresAt = payload.exp * 1000;
      const now = Date.now();
      const lifetime = expiresAt - now;
      if (lifetime <= 0) return;
      // First attempt at 80% of lifetime; retries use fixed short delay
      const isRetry = refreshRetryCountRef.current > 0;
      const delay = isRetry
        ? Math.min(PROACTIVE_RETRY_DELAY, lifetime - 5_000) // retry sooner, but leave 5s buffer
        : Math.max(lifetime * 0.8, 30_000);
      if (delay <= 0) return;
      refreshTimerRef.current = setTimeout(async () => {
        const refreshed = await attemptSilentRefresh();
        if (refreshed) {
          refreshRetryCountRef.current = 0;
          const newToken = tokenStorage.getToken();
          if (newToken) {
            setToken(newToken);
            scheduleTokenRefresh(newToken);
          }
        } else if (refreshRetryCountRef.current < MAX_PROACTIVE_RETRIES) {
          // Retry — the 401 handler in httpClient is the last line of defence
          refreshRetryCountRef.current++;
          scheduleRefreshAttempt(accessToken);
        }
      }, delay);
    } catch { /* malformed token — skip scheduling */ }
  };

  useEffect(() => {
    // Register logout event listener FIRST — before any async work or early returns
    const handleLogout = () => {
      setUser(null);
      setToken(null);
      setHasActiveSubscription(false);
      setIsAuthReady(false);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
    window.addEventListener('auth:logout', handleLogout);

    const initAuth = async () => {
      // Fast path: if in-memory token + user exist, render immediately
      const existingToken = tokenManager.getAccessToken();
      if (existingToken && !tokenManager.isTokenExpired(existingToken)) {
        const storedUser = tokenManager.getStoredUser();
        if (storedUser) {
          setToken(existingToken);
          setUser(convertAuthUser(storedUser));
          scheduleTokenRefresh(existingToken);
        }
      }

      // Always verify/restore session via backend — httpOnly cookies survive
      // page reloads even when the in-memory token store is empty (e.g. after
      // returning from Stripe checkout or any full-page navigation)
      const [userResult, accessResult] = await Promise.allSettled([
        authApi.getCurrentUser(),
        checkHasAccess(),
      ]);

      if (userResult.status === 'fulfilled') {
        setUser(convertAuthUser(userResult.value));

        // Check whether this is an impersonation session
        const currentToken = tokenStorage.getToken();
        if (currentToken) {
          try {
            const p = JSON.parse(atob(currentToken.split('.')[1]));
            if (p.impersonated_by) {
              setImpersonatedBy(p.impersonated_by);
            }
          } catch { /* ignore */ }
        }

        // If cookies worked but in-memory store is empty, repopulate via refresh
        if (!tokenStorage.getToken()) {
          const refreshed = await attemptSilentRefresh();
          if (refreshed) {
            const newToken = tokenStorage.getToken();
            if (newToken) {
              setToken(newToken);
              scheduleTokenRefresh(newToken);
            }
          }
        }
      }
      if (userResult.status === 'fulfilled' && isAdmin(convertAuthUser(userResult.value))) {
        setHasActiveSubscription(true);
      } else if (accessResult.status === 'fulfilled') {
        setHasActiveSubscription(accessResult.value);
      }
      // If getCurrentUser returned 401, httpClient already cleared tokens
      // and fired auth:logout — user state is already null

      setIsLoading(false);
      if (userResult.status === 'fulfilled') {
        setIsAuthReady(true);
      }
    };

    initAuth();

    return () => {
      window.removeEventListener('auth:logout', handleLogout);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    setIsLoading(true);

    try {
      const response = await authApi.login({ email, password });

      // Check if MFA is required
      if ('mfaChallengeToken' in response) {
        setMfaChallenge(response as MfaChallengeResponse);
        throw new MfaRequiredError();
      }

      const authResponse = response as import('../services/authApi').AuthResponse;
      setToken(authResponse.accessToken);
      setUser(convertAuthUser(authResponse.user));
      scheduleTokenRefresh(authResponse.accessToken);
      setIsAuthReady(true);

      // Store session start for timeout tracking
      try { sessionStorage.setItem('clinlexis_session_started_at', String(Date.now())); } catch { /* noop */ }

      // Admin users bypass payment flow entirely
      if (isAdmin(convertAuthUser(authResponse.user))) {
        setHasActiveSubscription(true);
      } else {
        setHasActiveSubscription(await checkHasAccess());
      }

    } catch (error) {
      // Let MFA challenges propagate to the caller
      if (error instanceof MfaRequiredError) {
        throw error;
      }

      console.error('AuthContext: Login failed:', error);

      // Preserve AuthApiError so callers can inspect status/code
      if (error instanceof AuthApiError) {
        throw error;
      }

      throw new AuthApiError(
        'Login failed. Please check your credentials and try again.',
        'LOGIN_FAILED',
        0
      );
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithTokens = (accessToken: string, refreshToken: string, authUser: import('../services/authApi').AuthUser) => {
    // Decode impersonated_by from JWT if present
    let isImpersonation = false;
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      if (payload.impersonated_by) {
        setImpersonatedBy(payload.impersonated_by);
        isImpersonation = true;
      }
    } catch { /* ignore */ }

    setToken(accessToken);
    setUser(convertAuthUser(authUser));
    scheduleTokenRefresh(accessToken);
    setIsAuthReady(true);
    setHasActiveSubscription(true); // Impersonation sessions bypass payment checks

    // Store session start for timeout tracking
    try { sessionStorage.setItem('clinlexis_session_started_at', String(Date.now())); } catch { /* noop */ }
  };

  const signUp = async (userData: any): Promise<void> => {
    setIsLoading(true);
    
    try {
      const response = await authApi.register({
        email: userData.email,
        password: userData.password,
        firstName: userData.firstName,
        lastName: userData.lastName,
        title: userData.title,
        gmcNumber: userData.gmcNumber,
        department: userData.hospital, // Map hospital to department
        specialization: userData.department, // Map department to specialization
        inviteCode: userData.inviteCode,
        personaType: userData.personaType,
      });
      
      setToken(response.accessToken);
      setUser(convertAuthUser(response.user));
      scheduleTokenRefresh(response.accessToken);
      setIsAuthReady(true);

      // Store session start for timeout tracking
      try { sessionStorage.setItem('clinlexis_session_started_at', String(Date.now())); } catch { /* noop */ }

    } catch (error) {
      console.error('❌ AuthContext: Registration failed:', error);
      
      if (error instanceof AuthApiError) {
        // Preserve the error details for field-level errors
        const enhancedError: any = new Error(error.message);
        enhancedError.details = error.details;
        enhancedError.code = error.code;
        throw enhancedError;
      }
      
      throw new Error('Registration failed. Please check your information and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const authUser = await authApi.getCurrentUser();
      const converted = convertAuthUser(authUser);
      setUser(converted);

      if (isAdmin(converted)) {
        setHasActiveSubscription(true);
      } else {
        setHasActiveSubscription(await checkHasAccess());
      }
      // Update stored user data
      const existingToken = tokenManager.getAccessToken();
      if (existingToken) {
        tokenManager.storeTokens({
          accessToken: existingToken,
          refreshToken: tokenManager.getRefreshToken() || '',
          tokenType: 'Bearer',
          expiresIn: 0,
          user: authUser,
        });
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const verifyMfa = async (code: string, method: MfaMethod | 'BACKUP', trustDevice: boolean): Promise<void> => {
    if (!mfaChallenge) throw new Error('No MFA challenge active');
    setIsLoading(true);
    try {
      const fingerprint = await getDeviceFingerprint();
      const authResponse = await mfaApi.verify({
        mfaChallengeToken: mfaChallenge.mfaChallengeToken,
        code,
        method,
        trustDevice,
        deviceFingerprint: fingerprint,
      });

      setMfaChallenge(null);
      setToken(authResponse.accessToken);
      setUser(convertAuthUser(authResponse.user));
      scheduleTokenRefresh(authResponse.accessToken);
      setIsAuthReady(true);

      // Store session start for timeout tracking
      try { sessionStorage.setItem('clinlexis_session_started_at', String(Date.now())); } catch { /* noop */ }

      if (isAdmin(convertAuthUser(authResponse.user))) {
        setHasActiveSubscription(true);
      } else {
        setHasActiveSubscription(await checkHasAccess());
      }
    } finally {
      setIsLoading(false);
    }
  };

  const sendMfaOtp = async (method: MfaMethod): Promise<void> => {
    if (!mfaChallenge) throw new Error('No MFA challenge active');
    const newToken = await mfaApi.sendOtp(mfaChallenge.mfaChallengeToken, method);
    setMfaChallenge({ ...mfaChallenge, mfaChallengeToken: newToken });
  };

  const clearMfaChallenge = () => {
    setMfaChallenge(null);
  };

  const confirmSubscription = () => {
    setHasActiveSubscription(true);
  };

  const logout = async (options?: { preserveAutoLogoutReason?: boolean }) => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setToken(null);
      setHasActiveSubscription(false);

      // Clear session timeout tracking. Manual sign-outs also clear the
      // auto-logout banner state so /signin renders cleanly; auto-expiry
      // callers pass preserveAutoLogoutReason so the banner survives the
      // logout teardown they triggered moments earlier.
      try { sessionStorage.removeItem('clinlexis_session_started_at'); } catch { /* noop */ }
      if (!options?.preserveAutoLogoutReason) {
        clearAutoLogoutReason();
      }

      // Clear user-specific localStorage to prevent state leaking across accounts
      const userScopedKeys = [
        'clinlexis_open_case_tabs',
        'clinlexis_redaction_mode',
        'clinlexis_privacy_mode',
        'clinlexis_document_view_mode',
        'clinlexis_date_format',
        'clinlexis_user_name',
        'ask-sidebar-collapsed',
        'sidebar-collapsed',
      ];
      userScopedKeys.forEach(key => {
        try { localStorage.removeItem(key); } catch { /* noop */ }
      });

      // Clear React Query cache to prevent stale data on re-login
      queryClient.clear();

      // Notify sibling tabs so they drop authenticated state too.
      // No reason field — this is a manual logout, not an auto-expiry.
      try {
        const channel = new BroadcastChannel(SESSION_BROADCAST_CHANNEL);
        channel.postMessage({ type: 'logout' });
        channel.close();
      } catch { /* BroadcastChannel unsupported — single-tab fallback */ }

      // Notify context providers to reset in-memory state
      window.dispatchEvent(new CustomEvent('auth:logout'));
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    isAuthReady,
    hasActiveSubscription,
    login,
    loginWithTokens,
    signUp,
    logout,
    refreshUser,
    confirmSubscription,
    token,
    isImpersonating: !!impersonatedBy,
    impersonatedBy,
    mfaChallenge,
    verifyMfa,
    sendMfaOtp,
    clearMfaChallenge,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};